import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/**
 * Word's line model on the canvas (studio.css, lineMetrics.js) works per run:
 * every run's box spans exactly what Word's line takes above and below the
 * baseline for that run, and the paragraph's own line box (the CSS strut)
 * must not add to it. So text without an ooxmlRun mark (a Word run with no
 * rPr, or text typed into an empty paragraph) renders inside a
 * `span.studio-run` decoration like every ooxmlRun span, and a textblock
 * holding text or a picture gets `studio-run-lines`, which zeroes its strut
 * (Word adds nothing below an inline picture either). An empty paragraph
 * keeps it: it is as tall as its mark in Word.
 * Purely a view concern: nothing reaches the document or the compiler.
 *
 * Each textblock's ranges are cached by node identity — an edit rebuilds the
 * set from the cache, so only changed paragraphs are scanned again.
 */

const RUN = { class: 'studio-run' }

const RUN_LINES = { class: 'studio-run-lines' }

const rangesCache = new WeakMap()

/**
 * A textblock's lines: whether runs or pictures make them (any), and the
 * offsets [from, to) of its unmarked text relative to its content start.
 */
function textOf(block) {
  let text = rangesCache.get(block)
  if (!text) {
    text = { any: false, bare: [] }
    block.forEach((child, offset) => {
      if (child.type.name === 'templateImage') text.any = true
      if (!child.isText) return
      text.any = true
      if (!child.marks.some((mark) => mark.type.name === 'ooxmlRun')) text.bare.push([offset, offset + child.nodeSize])
    })
    rangesCache.set(block, text)
  }
  return text
}

/** The decoration set of a whole document. */
export function textRunDecorations(doc) {
  const decorations = []
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true
    const text = textOf(node)
    if (text.any) decorations.push(Decoration.node(pos, pos + node.nodeSize, RUN_LINES))
    for (const [from, to] of text.bare) decorations.push(Decoration.inline(pos + 1 + from, pos + 1 + to, RUN))
    return false
  })
  return DecorationSet.create(doc, decorations)
}

const textRunsKey = new PluginKey('studioTextRuns')

export const TextRuns = Extension.create({
  name: 'studioTextRuns',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: textRunsKey,
        state: {
          init: (_, state) => textRunDecorations(state.doc),
          apply: (tr, set) => (tr.docChanged ? textRunDecorations(tr.doc) : set),
        },
        props: {
          decorations: (state) => textRunsKey.getState(state),
        },
      }),
    ]
  },
})
